// Workstream: growth. First-party, cookie-free analytics and client error reporting.
//
//   trackEvent('minigame_started', { game: 'charades' })
//
// Events go to POST /api/events with navigator.sendBeacon, so they never slow the page down and
// still arrive when the tab is closing. Page views are sent automatically on every route change,
// and any element with a data-cta="name" attribute reports a cta_click when tapped.
import { getDeviceId } from './storage';

/** Must match CLIENT_EVENTS in apps/server/src/growth/beacon.ts. */
export type ClientEvent =
  | 'page_view'
  | 'cta_click'
  | 'share_click'
  | 'minigame_started'
  | 'minigame_finished'
  | 'client_error';

export type EventProps = Record<string, string | number | boolean | undefined>;

const ENDPOINT = '/api/events';

/** Honour Do Not Track and Global Privacy Control for everything except crash reports. */
function optedOut(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean; doNotTrack?: string };
  return nav.globalPrivacyControl === true || nav.doNotTrack === '1';
}

function send(name: ClientEvent, props: EventProps = {}): void {
  try {
    const clean = Object.fromEntries(Object.entries(props).filter(([, v]) => v !== undefined));
    const body = JSON.stringify({ name, deviceId: getDeviceId(), path: location.pathname, props: clean });
    // text/plain keeps sendBeacon a "simple" request in every browser.
    const blob = new Blob([body], { type: 'text/plain' });
    if (navigator.sendBeacon?.(ENDPOINT, blob)) return;
    void fetch(ENDPOINT, { method: 'POST', body, headers: { 'content-type': 'text/plain' }, keepalive: true }).catch(() => {});
  } catch {
    // Analytics must never break the app.
  }
}

/** Record a product event. Safe to call anywhere; it never throws. */
export function trackEvent(name: ClientEvent, props?: EventProps): void {
  if (name !== 'client_error' && optedOut()) return;
  send(name, props);
}

// ------------------------------------------------------------------ page views

let lastPath = '';
let firstView = true;

/** Where the visitor came from: a utm_source, or another site's host. Nothing for internal links. */
function source(): string | undefined {
  const utm = new URLSearchParams(location.search).get('utm_source');
  if (utm) return utm.slice(0, 60);
  try {
    const ref = document.referrer ? new URL(document.referrer) : null;
    if (ref && ref.host !== location.host) return ref.host.replace(/^www\./, '');
  } catch {
    // Unparseable referrer.
  }
  return undefined;
}

function pageView(): void {
  const path = location.pathname;
  if (path === lastPath) return;
  lastPath = path;
  // Admin pages are ours, not visitors'.
  if (path.startsWith('/admin')) return;
  trackEvent('page_view', firstView ? { entry: true, ref: source() } : {});
  firstView = false;
}

// ------------------------------------------------------------------ error reporting

const MAX_ERRORS = 5;
const reported = new Set<string>();

function reportError(message: string, stack?: string): void {
  if (reported.size >= MAX_ERRORS || reported.has(message)) return;
  // Noise from browser extensions and cross-origin scripts carries no useful detail.
  if (!message || message === 'Script error.' || /extension:\/\//.test(stack ?? '')) return;
  reported.add(message);
  trackEvent('client_error', { message: message.slice(0, 300), stack: stack?.slice(0, 1500) });
}

function describe(value: unknown): { message: string; stack?: string } {
  if (value instanceof Error) return { message: `${value.name}: ${value.message}`, stack: value.stack };
  try {
    return { message: typeof value === 'string' ? value : JSON.stringify(value) ?? String(value) };
  } catch {
    return { message: String(value) };
  }
}

// ------------------------------------------------------------------ install

let installed = false;

/** Start page-view tracking, CTA click tracking and error reporting. Idempotent. */
export function installAnalytics(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // React Router navigates with pushState/replaceState, which fire no event of their own.
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = history[method].bind(history);
    history[method] = (...args: Parameters<History['pushState']>) => {
      original(...args);
      queueMicrotask(pageView);
    };
  }
  window.addEventListener('popstate', pageView);
  pageView();

  document.addEventListener(
    'click',
    (e) => {
      const el = (e.target as Element | null)?.closest?.('[data-cta]');
      const cta = el?.getAttribute('data-cta');
      if (cta) trackEvent('cta_click', { cta: cta.slice(0, 60) });
    },
    { capture: true, passive: true },
  );

  window.addEventListener('error', (e) => {
    const { message, stack } = e.error ? describe(e.error) : { message: e.message, stack: `${e.filename}:${e.lineno}:${e.colno}` };
    reportError(message, stack);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const { message, stack } = describe(e.reason);
    reportError(`Unhandled rejection: ${message}`, stack);
  });
}
